import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItem,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Rating,
  Select,
  Snackbar,
  TextField,
} from "@mui/material";
import axios from "axios.js";
import usePageTitle from "app/pageTitle";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";
// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import { useAppLocation } from "agent_location_context";
import { useAwaitDialog } from "app/components/status_dialog";
import DataFormComponent from "app/components/data_forms/data_form";
import QRCode from "react-qr-code";

function PSAFeedbackPage() {
  const pageTitle = `Product Sampling - Feedback`;
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const { applocation, permissionStatus, updateAppLocation } = useAppLocation();
  const statusDialog = useAwaitDialog();
  const dataFormRef = useRef();

  const [pageLoaded, setPageLoaded] = useState(false);
  const [resultData, setResultData] = useState({});
  const [productsFeedback, setProductsFeedback] = useState({});
  const [showQRDialog, setShowQRDialog] = useState(false);

  useEffect(() => {
    updateAppLocation();
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`agents/psa/${ref}/feedback/init.json`)
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            setResultData(result.data.data);
            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine
                ? ""
                : "Make sure you have an active internet connection"
            }`,
          );
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  function submitFeedback() {
    // Validate ratings
    for (var i = 0; i < resultData.products.length; i++) {
      var p = resultData.products[i];
      var pf = productsFeedback[p.reference];

      if (!pf || !pf.rating || pf.rating <= 0) {
        setSeverity("error");
        setErrMsg("Please rate all sampled products.");
        setOpen(true);
        return;
      }
    }

    // Get form data
    let formValues = null;
    if (resultData.data_form !== null) {
      const formResult = dataFormRef.current.submit();
      if (formResult.error) {
        setSeverity("error");
        setErrMsg(formResult.error);
        setOpen(true);
        return;
      }
      formValues = formResult.values;
    }

    var products = resultData.products.map(function (p) {
      var pf = productsFeedback[p.reference] || {};

      return {
        reference: p.reference,
        rating: pf.rating || 0,
        would_buy: pf.would_buy || "",
        would_recommend: pf.would_recommend || "",
        comment: pf.comment || "",
      };
    });

    var payload = {
      reference: ref,
      products: products,
      data_form: formValues,
    };

    setShowLoading(true);
    axios
      .post("agents/psa/feedback/submit.json", payload)
      .then(async function (result) {
        setShowLoading(false);
        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success");
          navigate(
            `/activations/product-sampling/${result.data.data.reference}`,
          );
        } else {
          await statusDialog.show(result.data.data.message, "error", "Failed");
        }
      })
      .catch(async function () {
        setShowLoading(false);
        await statusDialog.show(
          "Unable to complete request.",
          "error",
          "Failed",
        );
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={showQRDialog}>
        <DialogContent>
          <Box p={2} align="center">
            <QRCode value={resultData.link} />
            <MDTypography variant="subtitle2" mt={1}>Feedback Link</MDTypography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            color="dark"
            onClick={() => setShowQRDialog(false)}
            startIcon={<Icon>close</Icon>}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <MDBox mt={1} mb={5}>
        {pageLoaded && (
          <MDBox>
            <MDBox>
              <MDBox display="flex" justifyContent="space-between" mb={2}>
                <MDBox>
                  <Button
                    color="dark"
                    size="small"
                    startIcon={<Icon>west</Icon>}
                    onClick={() => {
                      if (window.history.length > 1) {
                        window.history.back();
                      } else {
                        navigate(`/activations/product-sampling/${ref}`);
                      }
                    }}
                  >
                    Back
                  </Button>
                </MDBox>
                <MDBox>
                  <Button
                    color="dark"
                    size="small"
                    startIcon={<Icon>qr_code</Icon>}
                    onClick={() => setShowQRDialog(true)}
                  >
                    QR Code
                  </Button>
                </MDBox>
              </MDBox>
              <Card>
                <MDBox p={2}>
                  <MDBox align="center">
                    <MDTypography variant="caption" color="text">
                      Campaign
                    </MDTypography>
                    <MDTypography variant="h5">
                      {resultData.campaign}
                    </MDTypography>
                  </MDBox>

                  <MDBox>
                    <Divider />
                  </MDBox>

                  <MDBox mt={3}>
                    {resultData.products.map((product, idx) => (
                      <MDBox mb={3} key={product.reference}>
                        <MDTypography
                          variant="h6"
                          fontWeight="regular"
                          fontSize={16}
                        >
                          {++idx}. {product.title}
                        </MDTypography>
                        <MDBox mt={1.5} mb={2}>
                          <MDTypography component="legend" fontSize={14}>
                            Rate
                          </MDTypography>
                          <Rating
                            name={`rating_${product.reference}`}
                            value={
                              productsFeedback[product.reference]?.rating || 0
                            }
                            onChange={(event, newValue) => {
                              setProductsFeedback((prev) => ({
                                ...prev,
                                [product.reference]: {
                                  ...prev[product.reference],
                                  rating: newValue,
                                },
                              }));
                            }}
                          />
                        </MDBox>
                        <MDBox mt={2}>
                          <MDTypography fontSize={14}>
                            Buy Willingness
                          </MDTypography>
                          <RadioGroup
                            row
                            value={
                              productsFeedback[product.reference]?.would_buy ??
                              ""
                            }
                            onChange={(e) =>
                              setProductsFeedback((prev) => ({
                                ...prev,
                                [product.reference]: {
                                  ...prev[product.reference],
                                  would_buy: e.target.value,
                                },
                              }))
                            }
                            orientation="vertical"
                          >
                            <FormControlLabel
                              value="yes"
                              control={<Radio />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="maybe"
                              control={<Radio />}
                              label="Maybe"
                            />
                            <FormControlLabel
                              value="no"
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                        </MDBox>
                        <MDBox mt={2}>
                          <MDTypography fontSize={14}>
                            Recommend Willingness
                          </MDTypography>
                          <RadioGroup
                            row
                            value={
                              productsFeedback[product.reference]
                                ?.would_recommend ?? ""
                            }
                            onChange={(e) =>
                              setProductsFeedback((prev) => ({
                                ...prev,
                                [product.reference]: {
                                  ...prev[product.reference],
                                  would_recommend: e.target.value,
                                },
                              }))
                            }
                            orientation="vertical"
                          >
                            <FormControlLabel
                              value="yes"
                              control={<Radio />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="maybe"
                              control={<Radio />}
                              label="Maybe"
                            />
                            <FormControlLabel
                              value="no"
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                        </MDBox>
                        <MDBox mt={2}>
                          <MDTypography fontSize={14}>
                            Customer comments (optional)
                          </MDTypography>
                          <MDInput
                            label="Comments (Optional)"
                            type="text"
                            multiline
                            minRows={2}
                            fullWidth
                            value={
                              productsFeedback[product.reference]?.comment || ""
                            }
                            onChange={(e) =>
                              setProductsFeedback((prev) => ({
                                ...prev,
                                [product.reference]: {
                                  ...prev[product.reference],
                                  comment: e.target.value,
                                },
                              }))
                            }
                          />
                        </MDBox>
                      </MDBox>
                    ))}
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
            {resultData.data_form && (
              <MDBox mt={3}>
                <DataFormComponent
                  ref={dataFormRef}
                  dataForm={resultData.data_form}
                />
              </MDBox>
            )}
            <MDBox mt={5} mb={1}>
              <Card>
                <MDBox p={3}>
                  <MDButton
                    variant="contained"
                    color="primary"
                    onClick={submitFeedback}
                    fullWidth
                  >
                    Submit Feedback
                  </MDButton>
                </MDBox>
              </Card>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PSAFeedbackPage;
